import {createReducer, on} from "@ngrx/store";
import {Comment} from "../generated-sources/api";
import {DatasetActions} from "../../import/store/import.actions";
import {CommentCountsActions, CommentHistoryActions, CommentInboxActions, CommentThreadActions} from "./comment.actions";
import {CommentState, INITIAL_COMMENT_STATE, INITIAL_THREAD_MODEL} from "./comment.state";

/** Resolution-driven badge count: root threads that still need attention. */
export const countUnresolved = (comments: Comment[]): number =>
    comments.filter((c) => c.needsResolution && !c.resolved && !c.deleted).length;

/** Icon-presence count: all non-deleted root threads, regardless of resolution. */
export const countTotal = (comments: Comment[]): number =>
    comments.filter((c) => !c.deleted).length;

const threadModel = (state: CommentState, context: string) =>
    state.threads[context] ?? INITIAL_THREAD_MODEL;

export const commentReducer = createReducer<CommentState>(
    INITIAL_COMMENT_STATE,

    // ---------- per-context thread ----------
    on(CommentThreadActions.load, (state, {context}) => ({
        ...state,
        threads: {
            ...state.threads,
            [context]: {...threadModel(state, context), status: "LOADING", errorMessage: ""},
        },
    })),
    on(CommentThreadActions.loadSuccess, (state, {context, comments}) => ({
        ...state,
        threads: {
            ...state.threads,
            [context]: {data: comments, status: "LOADED", errorMessage: ""},
        },
        // Keep the badge and the has-comment icon for this context in sync with the freshly loaded
        // thread so they stay correct after every mutation (which triggers a thread reload). This is
        // what makes the icon appear right after the first comment, without a counts reload.
        counts: {...state.counts, [context]: countUnresolved(comments)},
        totals: {...state.totals, [context]: countTotal(comments)},
    })),
    on(CommentThreadActions.loadError, (state, {context, errorMsg}) => ({
        ...state,
        threads: {
            ...state.threads,
            [context]: {...threadModel(state, context), status: "ERROR", errorMessage: errorMsg},
        },
    })),

    // ---------- deep-link "open thread on arrival" (one-shot) ----------
    on(CommentThreadActions.requestOpen, (state, {context}) => ({...state, requestedOpenContext: context})),
    on(CommentThreadActions.openHandled, (state) => ({...state, requestedOpenContext: null})),

    // ---------- bulk counts ----------
    // Merge (not replace): a page loads counts once at its root prefix, and the server returns one
    // row per context under it. Contexts that drop to zero are corrected by the exact-context path
    // in CommentThreadActions.loadSuccess (fired after every mutation). `total` powers the icon;
    // `unresolvedCount` powers the badge.
    on(CommentCountsActions.loadSuccess, (state, {counts}) => ({
        ...state,
        counts: counts.reduce<Record<string, number>>(
            (acc, c) => {
                acc[c.context] = c.unresolvedCount;
                return acc;
            },
            {...state.counts}
        ),
        totals: counts.reduce<Record<string, number>>(
            (acc, c) => {
                acc[c.context] = c.total;
                return acc;
            },
            {...state.totals}
        ),
    })),

    // Comment counts/totals are dataset-specific — a dataset switch invalidates them so stale
    // has-comment icons from the previous dataset don't linger. Each detail page re-dispatches a
    // counts load for the newly selected dataset (its counts subscription keys on id + dataset).
    on(DatasetActions.setsourceandtarget, (state) => ({...state, counts: {}, totals: {}})),

    // ---------- inbox ----------
    on(CommentInboxActions.load, (state) => ({
        ...state,
        inbox: {...state.inbox, status: "LOADING", errorMessage: ""},
    })),
    on(CommentInboxActions.loadSuccess, (state, {inbox}) => ({
        ...state,
        inbox: {data: inbox, status: "LOADED", errorMessage: ""},
    })),
    on(CommentInboxActions.loadError, (state, {errorMsg}) => ({
        ...state,
        inbox: {...state.inbox, status: "ERROR", errorMessage: errorMsg},
    })),

    // ---------- version history ----------
    on(CommentHistoryActions.load, (state) => ({
        ...state,
        history: {...state.history, status: "LOADING", errorMessage: ""},
    })),
    on(CommentHistoryActions.loadSuccess, (state, {entries}) => ({
        ...state,
        history: {data: entries, status: "LOADED", errorMessage: ""},
    })),
    on(CommentHistoryActions.loadError, (state, {errorMsg}) => ({
        ...state,
        history: {...state.history, status: "ERROR", errorMessage: errorMsg},
    })),
    on(CommentHistoryActions.reset, (state) => ({
        ...state,
        history: {data: [], status: "INIT", errorMessage: ""},
    })),
);
